// src/redux/slices/invoicesSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import InvoicesService from "../../services/api/invoicesService";
import { unauthorizedError } from "../../utils/helpers";
import { setSessionExpired, setTotalPage } from "./globalSlice";

const Invoices = new InvoicesService();

export const getInvoices = createAsyncThunk(
  "invoices/getAll",
  async (
    { page, limit, navigate, searchQuery, sortKey, sortDirection },
    { dispatch }
  ) => {
    try {
      const response = await Invoices.getAllInvoices({
        page,
        limit
      });
      if (response) {        
        dispatch(setTotalPage(response?.total));
        return response.data;
      }
      throw new Error("Failed to fetch invoices");
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error.data.error|| "Failed to fetch invoices");
      }
      throw error;
    }
  }
);
export const getInvoicesByOrgID = createAsyncThunk(
  "invoices/getAllbyOrgId",
  async (
    { page, limit, navigate,orgId },
    { dispatch }
  ) => {
    try {
      const response = await Invoices.getAllInvoicesByOrgId({
        page,
        limit,
        orgId
      });
      if (response) {
        dispatch(setTotalPage(response?.total));
        return response;
      }
      throw new Error("Failed to fetch invoices");
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error.data.error || "Failed to fetch invoices");
      }
      throw error;
    }
  }
);

export const sendInvoiceForApproval = createAsyncThunk(
  "invoices/sendForApproval",
  async ({ invoiceId,navigate},  { dispatch }) => {
  
    
    try {
      const response = await Invoices.approvalInvoice(invoiceId);
      toast.success(response?.message);
      return response;
    } catch (error) {
      toast.error(error.data.message);
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      toast.error(error.data.error);
      throw error;
    }
  }
);

export const getInvoiceApproval = createAsyncThunk(
  "invoices/getApproval",
  async (
    { page, limit, navigate },
    { dispatch }
  ) => {
    try {
      const response = await Invoices.getInvoiceApprovalList({
        page,
        limit,
      });
      if (response) {
        dispatch(setTotalPage(response?.total));
        return response.data;
      }
      throw new Error("Failed to fetch invoices");
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error.data.error|| "Failed to fetch invoices list");
      }
      throw error;
    }
  }
);

export const updateInvoiceStatus = createAsyncThunk(
  "invoices/updateStatus",
  async ({ invoiceId, status, navigate }, { dispatch }) => {
    try {
      const response = await Invoices.updateInvoiceStatus(invoiceId, status);
      toast.success(response?.message);
      return { invoiceId, status };
    } catch (error) {
      toast.error(error.data.error);
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }else {
        toast.error(error.data.error);
      }
      throw error;
    }
  }
);

const initialState = {
  loading: false,
  invoices: null,
  approvalInvoices: null,
  downloadLoading: false,
  error: null,
  page: 1,
  limit: 10,
  searchQuery: "",
};

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    setInvoicePage: (state, action) => {
      state.page = action.payload;
    },
    setInvoiceLimit: (state, action) => {
      state.limit = action.payload;
    },
    setInvoiceSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    resetInvoicesState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get Invoices reducers
      .addCase(getInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get Invoices by Org ID reducers
      .addCase(getInvoicesByOrgID.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoicesByOrgID.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.data;
      })
      .addCase(getInvoicesByOrgID.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Send Invoice for Approval reducers
      .addCase(sendInvoiceForApproval.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendInvoiceForApproval.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendInvoiceForApproval.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Invoice Approval List reducers
      .addCase(getInvoiceApproval.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoiceApproval.fulfilled, (state, action) => {
        state.loading = false;
        state.approvalInvoices = action.payload;
      })
      .addCase(getInvoiceApproval.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update Invoice Status reducers
      .addCase(updateInvoiceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (state.approvalInvoices) {
          state.approvalInvoices = state.approvalInvoices.map(invoice => {
            if (invoice._id === action.payload.invoiceId) {
              return {
                ...invoice,
                invoiceId: {
                  ...invoice.invoiceId,
                  status: action.payload.status
                }
              };
            }
            return invoice;
          });
        } 
      })
      .addCase(updateInvoiceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
  },
});

export const {
  setInvoicePage,
  setInvoiceLimit,
  setInvoiceSearchQuery,
  resetInvoicesState,
} = invoicesSlice.actions;

export default invoicesSlice.reducer;